import { authApi } from '@afinius/api-client/auth/api';
import {
  getLandlordBearerToken,
  getRefreshToken,
  getTenantRefreshToken,
  setBearerToken,
  setBearerTokenCookie,
  setLandlordBearerToken,
  setRefreshToken,
  setTenantRefreshToken,
} from '@afinius/api-client/bearer-token';
import { landlordAuthApi } from '@afinius/api-client/landlord-auth/api';
import { getTenantUuid } from '@afinius/api-client/tenant-uuid';
import type { ApiError } from '@afinius/types/Error';

import { isCapacitor } from './mobile';

export const refreshExpirationMinutes = () => (isCapacitor ? 43200 : 480);

export const REFRESH_LOCK_NAME = 'afinius-landlord-refresh';
export const TENANT_REFRESH_LOCK_NAME = 'afinius-tenant-refresh';

const withRefreshLock = async <T>(lockName: string, fn: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return await navigator.locks.request(lockName, fn);
  }

  return fn();
};

export type RefreshResult = 'recovered' | 'expired' | 'unavailable';

const classifyRefreshError = (error: unknown): 'expired' | 'unavailable' => {
  const status = (error as ApiError).response?.status;

  return status === 401 || status === 403 ? 'expired' : 'unavailable';
};

export const refreshTenantToken = async (): Promise<RefreshResult> => {
  return withRefreshLock(TENANT_REFRESH_LOCK_NAME, async () => {
    const tenantRefreshToken = getTenantRefreshToken();
    let refreshUnavailable = false;

    if (tenantRefreshToken) {
      try {
        const response = await authApi.refresh({ refresh_token: tenantRefreshToken });

        setBearerToken(`Bearer ${response.access_token}`, response.expires_in);
        setTenantRefreshToken(
          response.refresh_token ?? undefined,
          response.refresh_expires_in ?? undefined,
        );

        return 'recovered';
      } catch (error) {
        const outcome = classifyRefreshError(error);
        if (outcome === 'expired') {
          setTenantRefreshToken(undefined);
        } else {
          refreshUnavailable = true;
        }
      }
    }

    if (getLandlordBearerToken() && (!isCapacitor || getTenantUuid())) {
      try {
        const { token } = await landlordAuthApi.ssoInitiate();
        const response = await authApi.ssoVerify({
          token,
          refresh_expiration: refreshExpirationMinutes(),
        });

        setBearerToken(`Bearer ${response.access_token}`, response.expires_in);
        setTenantRefreshToken(
          response.refresh_token ?? undefined,
          response.refresh_expires_in ?? undefined,
        );

        return 'recovered';
      } catch (error) {
        if (classifyRefreshError(error) === 'unavailable') {
          refreshUnavailable = true;
        }
      }
    }

    return refreshUnavailable ? 'unavailable' : 'expired';
  });
};

export const refreshLandlordToken = async (): Promise<RefreshResult> => {
  return withRefreshLock(REFRESH_LOCK_NAME, async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return 'expired';

    try {
      const response = await landlordAuthApi.refresh({
        refresh_token: refreshToken,
      });

      setLandlordBearerToken(`Bearer ${response.access_token}`, response.expires_in);
      setBearerTokenCookie(`Bearer ${response.access_token}`);
      setRefreshToken(response.refresh_token, response.refresh_expires_in);

      return 'recovered';
    } catch (error) {
      return classifyRefreshError(error);
    }
  });
};

const SILENT_SSO_TIMEOUT_MS = 2500;

export const classifySsoVerifyError = (error: unknown): 'denied' | 'failed' =>
  (error as ApiError).response?.status === 403 ? 'denied' : 'failed';

const SSO_REDIRECT_ATTEMPTED_KEY = 'sso-redirect-attempted';

export const redirectSso = (authOrigin: string, ssoInitiateUrl: string) => {
  const frameUrl =
    `${authOrigin}/sso-handler.html` +
    `?ssoInitiateUrl=${encodeURIComponent(ssoInitiateUrl)}` +
    `&redirectTo=${encodeURIComponent(location.href)}`;

  window.location.href = frameUrl;
};

export const attemptSsoRedirect = (authOrigin: string, ssoInitiateUrl: string): boolean => {
  try {
    if (sessionStorage.getItem(SSO_REDIRECT_ATTEMPTED_KEY)) return false;

    sessionStorage.setItem(SSO_REDIRECT_ATTEMPTED_KEY, '1');
    redirectSso(authOrigin, ssoInitiateUrl);

    return true;
  } catch {
    return false;
  }
};

export const clearSsoRedirectAttempt = (): void => {
  sessionStorage.removeItem(SSO_REDIRECT_ATTEMPTED_KEY);
};

export const redirectToLogin = (authOrigin: string, reason?: string): void => {
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';

  window.location.href = `${authOrigin}/auth/login${query}`;
};

const iframeSso = (authOrigin: string, ssoInitiateUrl: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const frameUrl = `${authOrigin}/sso-handler.html?origin=${encodeURIComponent(location.origin)}&ssoInitiateUrl=${encodeURIComponent(ssoInitiateUrl)}`;

    const iframe = document.createElement('iframe');
    iframe.src = frameUrl;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    let resolved = false;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
      iframe.remove();
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== authOrigin) return;

      resolved = true;
      cleanup();

      if (event.data?.type === 'ssoToken' && event.data.token) {
        resolve(event.data.token as string);
      } else {
        resolve(null);
      }
    };

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        cleanup();
        resolve(null);
      }
    }, SILENT_SSO_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
  });
};

export const silentSso = async (
  authOrigin: string,
  ssoInitiateUrl: string,
): Promise<string | null> => {
  const urlParams = new URLSearchParams(location.search);
  const ssoToken = urlParams.get('ssoToken');

  if (ssoToken) {
    urlParams.delete('ssoToken');
    const cleanedUrl = location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
    history.replaceState({}, '', cleanedUrl);
    return Promise.resolve(ssoToken);
  }

  const token = await iframeSso(authOrigin, ssoInitiateUrl);
  if (token) return token;

  redirectSso(authOrigin, ssoInitiateUrl);

  return null;
};
